import PropTypes from 'prop-types';

import useTranslations from '@/i18n/useTranslations';
import XIcon from '@/shared-components/icons/components/public/XIcon';

const LeaderboardHeader = ({ t }) => (
  <div className="color-mix__leaderboard-header" role="row">
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--rank">#</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--name">{t('columns.user')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--streak">{t('columns.best')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--points">{t('columns.points')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--pours">{t('columns.pours')}</span>
  </div>
);

const LeaderboardRow = ({ entry, highlight = false }) => (
  <div className={`color-mix__leaderboard-row${highlight ? ' is-highlighted' : ''}`} role="row">
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--rank">{entry.rank}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--name">{entry.displayName}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--streak">{entry.bestStreak}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--points">{entry.totalPoints}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--pours">{entry.totalPours}</span>
  </div>
);

LeaderboardRow.propTypes = {
  entry: PropTypes.shape({
    rank: PropTypes.number,
    displayName: PropTypes.string,
    bestStreak: PropTypes.number,
    totalPoints: PropTypes.number,
    totalPours: PropTypes.number,
  }).isRequired,
  highlight: PropTypes.bool,
};

export default function LeaderboardPanel({ entries = [], userRow = null, isLoading = false, error = null, onClose = null }) {
  const t = useTranslations('common.newspaper.art_games.color_mix.leaderboard');
  const userInTop = Boolean(userRow?.id && entries.some((entry) => entry.id === userRow.id));

  return (
    <section className="color-mix__leaderboard" aria-label={t('title')}>
      <div className="generic-popup__header color-mix__leaderboard-heading">
        <h2 className="generic-popup__header-text">{t('title')}</h2>
        {onClose ? (
          <XIcon
            className="generic-popup__close"
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClose();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('close_aria')}
          />
        ) : null}
      </div>
      {error ? <p role="alert" className="color-mix__leaderboard-error">{error.message}</p> : null}
      {entries.length === 0 && !isLoading ? (
        <p className="color-mix__leaderboard-empty">{t('empty')}</p>
      ) : (
        <div className="color-mix__leaderboard-table" role="table" aria-label={t('table_aria')} tabIndex={0}>
          <div className="color-mix__leaderboard-grid">
            <LeaderboardHeader t={t} />
            <div role="rowgroup">
              {entries.map((entry) => (
                <LeaderboardRow key={entry.id || entry.rank} entry={entry} highlight={Boolean(userRow?.id && entry.id === userRow.id)} />
              ))}
            </div>
          </div>
        </div>
      )}
      {userRow && !userInTop ? (
        <div className="color-mix__leaderboard-user" aria-label={t('your_ranking')}>
          <h3>{t('your_ranking')}</h3>
          <div className="color-mix__leaderboard-user-card">
            <strong>#{userRow.rank}</strong>
            <span className="color-mix__leaderboard-user-name">{userRow.displayName}</span>
            <span>{t('columns.best')}: {userRow.bestStreak}</span>
            <span>{t('columns.points')}: {userRow.totalPoints}</span>
            <span>{t('columns.pours')}: {userRow.totalPours}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

LeaderboardPanel.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object),
  userRow: PropTypes.shape({
    id: PropTypes.string,
    rank: PropTypes.number,
    displayName: PropTypes.string,
    bestStreak: PropTypes.number,
    totalPoints: PropTypes.number,
    totalPours: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onClose: PropTypes.func,
};
